import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { servicePages } from '../data/servicePageData';
import { companyInfo } from '../mock';
import Header from '../components/Header';

const ServicePage = ({ slug }) => {
  const navigate = useNavigate();
  const data = servicePages[slug];

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Update document title + meta description
  useEffect(() => {
    if (!data) return;
    document.title = data.metaTitle;
    let metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', data.metaDescription);

    // Inject Service schema
    const existingSchema = document.getElementById('service-schema');
    if (existingSchema) existingSchema.remove();
    const script = document.createElement('script');
    script.id = 'service-schema';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': `${data.schema.url}#service`,
      name: data.schema.name,
      serviceType: data.schema.serviceType,
      description: data.schema.description,
      url: data.schema.url,
      provider: { '@id': 'https://sgsglobalmedia.com/#organization' },
      areaServed: [
        { '@type': 'Country', name: 'India' },
        { '@type': 'Country', name: 'United States' },
        { '@type': 'Country', name: 'United Kingdom' },
        { '@type': 'Country', name: 'Australia' },
      ],
    });
    document.head.appendChild(script);

    // Restore on unmount
    return () => {
      script.remove();
    };
  }, [data]);

  if (!data) {
    navigate('/');
    return null;
  }

  const goToSection = (sectionId) => {
    navigate('/');
    setTimeout(() => {
      if (sectionId && sectionId !== 'home') {
        const el = document.querySelector(sectionId);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 100);
  };

  const goToBooking = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.querySelector('#contact');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <div className="min-h-screen" style={{ background: '#020617', color: '#94a3b8', fontFamily: "'DM Sans', sans-serif" }}>

      <Header onBooking={goToBooking} onSectionClick={goToSection} />

      {/* ── Hero ── */}
      <header className="relative pt-56 pb-24 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-[#007bff]/10 blur-[150px] rounded-full pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex justify-center gap-2 text-xs text-gray-500 mb-6">
            <a href="/" onClick={e => { e.preventDefault(); goHome(); }} className="hover:text-[#007bff] transition-colors">Home</a>
            <span>/</span>
            <span className="text-[#007bff]">{data.hero.label}</span>
          </nav>

          <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full mb-6 border border-[#007bff]/20 text-xs font-bold uppercase tracking-widest text-[#3399ff]" style={{ background: 'rgba(0,123,255,0.07)' }}>
            {data.hero.label}
          </div>

          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-6 tracking-tighter leading-[1.1]" style={{ fontFamily: "'Syne', sans-serif" }}>
            {data.hero.heading}
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto mb-10 leading-relaxed">
            {data.hero.subheading}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button
              onClick={() => goHome('/#contact')}
              className="bg-[#007bff] text-white font-black px-10 py-4 rounded-2xl text-lg glow-button"
            >
              Book Free Consultation
            </button>
            <button
              onClick={() => goHome('/#services')}
              className="glass text-white font-bold px-10 py-4 rounded-2xl text-lg hover:border-[#007bff]/50 transition-all"
            >
              All Services
            </button>
          </div>
        </div>
      </header>

      {/* ── Overview ── */}
      <section className="py-20 px-6" style={{ background: '#020617' }}>
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#007bff] mb-3">Overview</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-8 tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            What We Do & How It Works
          </h2>
          <p className="text-gray-400 text-base leading-[1.9]">{data.overview}</p>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 px-6" style={{ background: '#020617' }}>
        <div className="max-w-7xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#007bff] mb-3">What's Included</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-12 tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            Everything You Get
          </h2>
          <div
            className="grid md:grid-cols-2 lg:grid-cols-3"
            style={{ gap: '1.5px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            {data.features.map((f, i) => (
              <div key={i} className="p-8" style={{ background: '#111620' }}>
                <div
                  className="w-10 h-10 rounded flex items-center justify-center text-[#007bff] font-black text-sm mb-4"
                  style={{ background: 'rgba(0,123,255,0.1)', border: '1px solid rgba(0,123,255,0.2)' }}
                >
                  0{i + 1}
                </div>
                <h3 className="text-white font-bold text-base mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>{f.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Process ── */}
      <section className="py-20 px-6" style={{ background: '#030a1a' }}>
        <div className="max-w-5xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#007bff] mb-3">Our Process</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-12 tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            How We Deliver Results
          </h2>
          <div className="space-y-6">
            {data.process.map((step, i) => (
              <div
                key={i}
                className="flex gap-6 p-8 rounded-2xl"
                style={{ background: '#111620', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="flex-shrink-0 text-3xl font-black" style={{ color: 'rgba(0,123,255,0.25)', fontFamily: "'Syne', sans-serif" }}>
                  {step.step}
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: "'Syne', sans-serif" }}>{step.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 px-6" style={{ background: '#020617' }}>
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#007bff] mb-3">FAQ</p>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white mb-12 tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {data.faqs.map((faq, i) => (
              <details
                key={i}
                className="group rounded-2xl p-6"
                style={{ background: '#111620', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <summary className="cursor-pointer font-semibold text-white text-base flex justify-between items-center gap-4 list-none">
                  {faq.q}
                  <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-lg font-light text-white transition-transform group-open:rotate-45" style={{ background: 'rgba(0,123,255,0.15)' }}>+</span>
                </summary>
                <p className="text-gray-400 text-sm leading-relaxed mt-4">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 px-6 text-center" style={{ background: '#030a1a' }}>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 tracking-tight" style={{ fontFamily: "'Syne', sans-serif" }}>
            Ready to Get Started?
          </h2>
          <p className="text-gray-400 text-lg mb-10">
            Book a free 30-minute consultation. We'll review your current situation, identify the biggest opportunities, and outline exactly how we'd approach your goals.
          </p>
          <button
            onClick={() => goHome('/#contact')}
            className="bg-[#007bff] text-white font-black px-12 py-5 rounded-2xl text-xl glow-button"
          >
            Book Free Consultation
          </button>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-10 px-6 border-t" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <span className="text-sm font-extrabold" style={{ color: 'rgba(255,255,255,0.4)' }}>{companyInfo.name}</span>
          <nav aria-label="Footer navigation" className="flex flex-wrap justify-center gap-6">
            {navLinks.map(link => (
              <button
                key={link.path}
                onClick={() => goHome(link.path.replace('/', ''))}
                className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 hover:text-[#007bff] transition-colors"
              >
                {link.label}
              </button>
            ))}
          </nav>
          <p className="text-gray-600 text-xs uppercase tracking-[0.2em]">© 2025 SGS Global Media</p>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-6 flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
          <address className="not-italic text-gray-600 text-xs text-center md:text-left leading-relaxed">
            <span className="block">{companyInfo.name}</span>
            <span className="block">{companyInfo.address}</span>
            <a href={`mailto:${companyInfo.email}`} className="hover:text-[#007bff] transition-colors">{companyInfo.email}</a>
          </address>
          <nav aria-label="Legal links" className="flex gap-6">
            <a href="/privacy-policy" className="text-xs text-gray-600 hover:text-[#007bff] transition-colors uppercase tracking-[0.15em]">Privacy Policy</a>
            <a href="/terms-of-service" className="text-xs text-gray-600 hover:text-[#007bff] transition-colors uppercase tracking-[0.15em]">Terms of Service</a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default ServicePage;
